/* eslint-disable no-param-reassign */
import Excel from "exceljs";
// @ts-expect-error: Ignoring because it wants us to update the tsconfig.json to include "resolveJsonModule", which is not worth it just for this
import filenamify from "filenamify/browser";
import dayjs from "dayjs";
import { downloadFile, saveAs } from "@/utils/blob";
import {
  EmployeeTimesheetData,
  EntryCategory,
  TimesheetEntry,
} from "../types/Timesheet";

const CategoryColumnMap: Record<EntryCategory, number> = {
  REGULAR: 3,
  OVERTIME: 4,
  DOUBLE: 5,
  FIELD: 6,
  FIELD_OVERTIME: 7,
  FIELD_DOUBLE: 8,
  TRAVEL: 9,
  VACATION: 10,
  SICK: 11,
  BANKED_HOURS: 12,
  LOA: 13,
};

interface HeaderArgs {
  worksheet: Excel.Worksheet;
  employeeInfo: {
    Name: string;
    Email: string;
    Location: string;
  };
  startDate: string;
  endDate: string;
}
const fillHeader = ({
  worksheet,
  employeeInfo,
  startDate,
  endDate,
}: HeaderArgs) => {
  for (let i = 7; i <= 13; i += 1) {
    worksheet.mergeCells(`A${i}:B${i}`);
  }

  worksheet.getCell("C7").value = startDate;
  worksheet.getCell("C8").value = endDate;

  worksheet.getCell("C9").value = employeeInfo.Name;
  worksheet.getCell("C10").value = employeeInfo.Email;
  worksheet.getCell("C11").value = employeeInfo.Location;
};

const insertDateRows = ({
  worksheet,
  startDate,
  endDate,
}: {
  worksheet: Excel.Worksheet;
  startDate: string;
  endDate: string;
}) => {
  // Insert date rows starting from row 14, pushing down the existing rows
  const start = dayjs(startDate);

  // Replace existing row 14 with the first date, then insert additional rows as needed
  const startRow = worksheet.getRow(14);
  startRow.getCell("A").value = start.format("dddd").toLocaleUpperCase();
  startRow.getCell("B").value = start.format("YYYY-MM-DD");
  startRow.commit();

  const end = dayjs(endDate);
  const totalDays = end.diff(start, "day") + 1;

  if (totalDays >= 1) {
    worksheet.duplicateRow(14, totalDays - 1, true);

    for (let i = 1; i < totalDays; i += 1) {
      const currentRow = worksheet.getRow(14 + i);
      const currentDate = start.add(i, "day");
      currentRow.getCell("A").value = currentDate
        .format("dddd")
        .toLocaleUpperCase();
      currentRow.getCell("B").value = currentDate.format("YYYY-MM-DD");
      currentRow.commit();
    }

    const sumRow = worksheet.getRow(14 + totalDays);
    // Add SUM formula for columns C to L
    for (let col = 3; col <= 12; col += 1) {
      const colLetter = worksheet.getColumn(col).letter;
      sumRow.getCell(col).value = {
        formula: `SUM(${colLetter}14:INDEX(${colLetter}:${colLetter}, ROW()-1))`,
      };
    }
    sumRow.commit();
  }
};

const fillTimesheetData = ({
  worksheet,
  timesheetEntries,
}: {
  worksheet: Excel.Worksheet;
  timesheetEntries: TimesheetEntry[];
}) => {
  timesheetEntries.forEach((entry) => {
    if (!entry.type) return;
    const column = CategoryColumnMap[entry.type];

    // Find the row with the matching date in column B
    const dateCell = worksheet
      .getColumn("B")
      .values.findIndex((value) => value === entry.date);

    if (dateCell > 0) {
      const cell = worksheet.getRow(dateCell).getCell(column);
      const existingValue = cell.value || 0;
      cell.value = Number(existingValue) + entry.hours + entry.minutes / 60;
    }

    // If entry type is LOA, also fill in column M with TRUE to indicate LOA day
    if (entry.type === "LOA" && dateCell > 0) {
      const loaCell = worksheet.getRow(dateCell).getCell(13); // Column M
      loaCell.value = "TRUE";
    }
  });
};

interface ExportArgs {
  startDate: string;
  endDate: string;
  employeeTimesheetData: EmployeeTimesheetData[];
}

export const exportTimesheetToXlsx = async ({
  startDate,
  endDate,
  employeeTimesheetData,
}: ExportArgs) => {
  const response = await downloadFile("/TimesheetTemplate.xlsx");
  if (!response) {
    throw new Error("Unable to download template for timesheet");
  }
  const blob = await response.blob();
  const workbook = new Excel.Workbook();
  const arrayBuffer = await blob.arrayBuffer();
  await workbook.xlsx.load(arrayBuffer);

  const templateSheet = workbook.getWorksheet("Template_Worksheet");
  if (!templateSheet) {
    throw new Error("Template sheet not found in the workbook");
  }

  // Ensure worksheet names are unique
  const usedNames = new Set<string>();
  employeeTimesheetData.forEach((item) => {
    let sheetName = filenamify(`${item.Name}_${item.Location}`);
    let counter = 1;
    while (usedNames.has(sheetName)) {
      sheetName = filenamify(`${counter}_${item.Name}_${item.Location}`);
      counter += 1;
    }
    usedNames.add(sheetName);

    // Create a new sheet for each employee by copying the template sheet
    const worksheet = workbook.addWorksheet("Temp name");
    worksheet.model = {
      ...templateSheet.model,
      name: sheetName,
    };

    fillHeader({
      worksheet,
      employeeInfo: {
        Name: item.Name,
        Email: item.Email,
        Location: item.Location,
      },
      startDate,
      endDate,
    });

    insertDateRows({
      worksheet,
      startDate,
      endDate,
    });

    fillTimesheetData({
      worksheet,
      timesheetEntries: item.Entries,
    });
  });

  // Remove the template sheet before saving
  workbook.removeWorksheet(templateSheet.id);

  const outputBuffer = await workbook.xlsx.writeBuffer();
  const output = new Blob([outputBuffer]);
  const fileName = `VV Timesheet ${startDate} - ${endDate}.xlsx`;

  await saveAs(output, fileName);
};
